import { useParams } from "react-router";
import { BackLink } from "components";
import React, { useState } from "react";
import { Heading, Loader, Popup } from "uikit/components";
import { useGetCoachStatisticsBySchool } from "hooks/coachStatistics";
import styles from "./CoachStatisticsPage.module.styl";
import Pagination from "uikit/components/Pagination/Pagination";
import math from "lodash";
import IconTooltip from "uikit/components/IconTooltip/IconTooltip";
import LegendBox from "../../../uikit/components/LegendBox/LegendBox";
import CoachStatisticsView from "../../../components/coachStatistics/CoachStatisticsView/CoachStatisticsView";
import { useContextPerson } from "hooks/persons";
import { Flags } from "utils";
import { Helmet } from "react-helmet-async";

export default function CoachStatisticsPage() {
    const pageSize = 20;

    const [payload, setPayload] = useState({ pageNumber: 1 });

    const { schoolId } = useParams();
    const { flags, isLoading: isContextLoading } = useContextPerson();
    const { isLoading, data } = useGetCoachStatisticsBySchool(schoolId, payload, {
        keepPreviousData: true,
    });

    const handlePageChange = (newPageNumber) => {
        setPayload((payload) => ({ ...payload, pageNumber: newPageNumber }));
    };

    const iconContent = (
        <div className={styles.infoLink} data-test-name="hint_statistics_statistics">
            Как считается статистика ?
        </div>
    );

    if (isLoading || isContextLoading) {
        return (
            <Popup blockClickOnOverlay appearance="adaptive">
                <Loader
                    position={"margin-auto"}
                    text={"Подождите. Данные загружаются."}
                    testName="loader-text"
                />
            </Popup>
        );
    }

    if (data) {
        return (
            <>
                <Helmet>
                    <meta
                        name="apple-mobile-web-app-title"
                        content={`Статистика работы тренеров в системе: ${data.schoolName}`}
                    />
                    <meta
                        name="application-name"
                        content={`Статистика работы тренеров в системе: ${data.schoolName}`}
                    />
                </Helmet>
                {!flags?.[Flags.HideDashboardBackButton] && (
                    <BackLink to={`/school-summary/${schoolId}`} text={"Назад"} name="back_link" />
                )}
                <Heading size={"h1"} className={styles.mainHeader} name="coach-statistics-title">
                    Статистика работы тренеров в системе: {data.schoolName}
                </Heading>
                <div className={styles.infoContainer}>
                    <div
                        className={styles.subHeader}
                        data-test-name={"coach-statistics-subtitle-1"}
                    >
                        Поддерживайте показатели работы тренеров на высоком уровне, чтобы ваша
                        организация стала лидером в регионе.
                    </div>
                    <div>
                        <IconTooltip trigger="hover" iconText={iconContent}>
                            <div className={styles.container}>
                                <div className={styles.infoHeader}>Расчёт статистики:</div>
                                <div className={styles.infoRow}>
                                    <div className={styles.infoSubHeader}>
                                        Заполнение планов тренировок
                                    </div>
                                    = доля тренировок с заполненным тренировочным планом
                                </div>
                                <div className={styles.infoRow}>
                                    <div className={styles.infoSubHeader}>
                                        Заполнение журналов тренировок{" "}
                                    </div>
                                    = доля тренировок с заполненной посещаемостью
                                </div>
                                <div className={styles.infoRow}>
                                    <div className={styles.infoSubHeader}>
                                        Заполнение результатов соревнований{" "}
                                    </div>
                                    = доля соревнований, в которых занесены результаты
                                </div>
                                <div className={styles.infoRow}>
                                    <div className={styles.infoSubHeader}>
                                        Сводный показатель работы в системе{" "}
                                    </div>
                                    = среднее значение по всем показателям статистики (если у
                                    тренера в расписании не было тренировок или соревнований, то
                                    связанные с ними показатели не учитываются при расчёте)
                                </div>
                            </div>
                        </IconTooltip>
                    </div>
                </div>
                <div className={styles.subHeader} data-test-name={"coach-statistics-subtitle-2"}>
                    Статистика рассчитывается за прошлую календарную неделю и обновляется ежедневно.
                </div>
                <div className={styles.legendContainer}>
                    <LegendBox
                        size={"small"}
                        color={"green"}
                        textSize={"small"}
                        text={"75 - 100%"}
                        dataTestName={"coach-statistics-green-hint"}
                    />
                    <LegendBox
                        size={"small"}
                        color={"orange"}
                        textSize={"small"}
                        text={"50 - 74%"}
                        dataTestName={"coach-statistics-orange-hint"}
                    />
                    <LegendBox
                        size={"small"}
                        color={"red"}
                        textSize={"small"}
                        text={"0 - 49%"}
                        dataTestName={"coach-statistics-red-hint"}
                    />
                </div>
                <CoachStatisticsView
                    coachesStatistics={data.coachesStatistics}
                    pageSize={pageSize}
                    pageNumber={payload.pageNumber}
                />
                {data.count > pageSize && (
                    <Pagination
                        pageCount={math.ceil(data.count / pageSize)}
                        forcePage={payload.pageNumber}
                        onPageChange={handlePageChange}
                        className={styles.pagination}
                    />
                )}
            </>
        );
    }
}
